import SpaceBetween from "@amzn/awsui-components-react/polaris/space-between";
import Container from "@amzn/awsui-components-react/polaris/container";
import Grid from "@amzn/awsui-components-react/polaris/grid";
import Box from "@amzn/awsui-components-react/polaris/box";
import Header from "@amzn/awsui-components-react/polaris/header";
import Button from "@amzn/awsui-components-react/polaris/button";
import Alert from "@amzn/awsui-components-react/polaris/alert";
import Input from "@amzn/awsui-components-react/polaris/input";
import FormField from "@amzn/awsui-components-react/polaris/form-field";
import styled from "styled-components";
import { CheckoutSession, CheckoutSessionType } from "../common/types";
import {
  useDiscountOperations,
  APPLIED_DISCOUNT_KEY,
  APPLIED_DISCOUNT_RESERVATION_KEY,
  APPLIED_DISCOUNT_AMOUNT,
  APPLIED_DISCOUNT_SESSION_ID,
} from "../hooks/useDiscountOperations";
import Term from "./Term";
import { useFinalizeCheckoutSession } from "../hooks/useFinalizeCheckoutSession";
import { Fragment, useState, useEffect } from "react";
import { createCheckoutEventTrigger } from "../analytics/createCheckoutEventTrigger";
import { useTranslation } from "react-i18next";
import "./styles.scss";
import i18n from "../../i18n/config";
import LoadingContent from "./LoadingContent";
import { ENABLE_DISCOUNT_FEATURE } from "../common/constants";
import { getNodeEnvironment } from "../common/nodeEnvironment";
import Checkbox from "@amzn/awsui-components-react/polaris/checkbox";
import { isFtv2SupportEnabled } from "../common/utilityFunctions";

/**
 * /////////////////////////////////////////////////////////////////////////////
 * BEFORE MAKING ANY UI RELATED CHANGES, FOLLOW FTC SNAPSHOT SOP FROM THE README
 * /////////////////////////////////////////////////////////////////////////////
 */

const SubmitButton = styled(Button)`
  width: 100%;
  text-align: center !important;
`;

interface FinalizeCheckoutSessionProps {
  checkoutSession: CheckoutSession;
  isFreeTrial: boolean;
  isPromotional: boolean;
  appliedBenefitIds: string[] | undefined;
  freeTrialEndsDate: string | undefined;
  freeTrialDuration: number | undefined;
  promotionalDiscount: number | undefined;
  promotionalEndsDate: string | undefined;
  isFreeTrialV2Account: boolean | undefined;
  finalizeSession: () => void;
  onClickContinueToAws: () => void;
}

const StatusMessage = styled.div<{ isError?: boolean }>`
  color: ${(props) => (props.isError ? "#d13212" : "#037f0c")};
  margin-top: 8px;
  font-size: 14px;
`;

const FinalizeCheckoutSession = ({
  checkoutSession,
  isFreeTrial,
  isPromotional,
  appliedBenefitIds,
  freeTrialEndsDate,
  freeTrialDuration,
  promotionalDiscount,
  promotionalEndsDate,
  isFreeTrialV2Account,
  onClickContinueToAws,
  finalizeSession,
}: FinalizeCheckoutSessionProps): JSX.Element => {
  const { t } = useTranslation();
  const dir = i18n.dir();
  const [isAgreedTos, setIsAgreedTos] = useState(false);
  const {
    shouldShowContinueToAws,
    finalizeCheckoutSessionComponent,
    displayPeriodIntlKey,
    estimatedTotalPeriodAmount,
    previousEstimatedTotalPeriodAmount,
    subtotal,
    pricePerUnit,
    quantity,
    applyBenefitToOfferLoading,
  } = useFinalizeCheckoutSession(
    checkoutSession,
    appliedBenefitIds,
    isPromotional
  );

  const { applyDiscount, cancelDiscountReservation } =
    useDiscountOperations(checkoutSession);

  const [discountCode, setDiscountCode] = useState("");
  const [isApplyingDiscount, setIsApplyingDiscount] = useState(false);
  const [appliedDiscount, setAppliedDiscount] = useState<{
    reservationId: string;
    amount: number;
    estimatedPayment: number;
    code?: string;
  } | null>(null);
  const [statusMessage, setStatusMessage] = useState<{
    text: string;
    isError: boolean;
  } | null>(null);

  // Initialize discount state from stored values if they belong to current session
  useEffect(() => {
    const storedCode = localStorage.getItem(APPLIED_DISCOUNT_KEY);
    const storedReservationId = localStorage.getItem(
      APPLIED_DISCOUNT_RESERVATION_KEY
    );
    const storedDiscountAmount = localStorage.getItem("appliedDiscountAmount");
    const storedSessionId = localStorage.getItem("appliedDiscountSessionId");

    // Only restore discount if it belongs to current session
    if (
      storedCode &&
      storedReservationId &&
      storedDiscountAmount &&
      storedSessionId === checkoutSession.id
    ) {
      const discountAmount = Number(storedDiscountAmount);
      setAppliedDiscount({
        reservationId: storedReservationId,
        amount: Number(subtotal) - discountAmount,
        estimatedPayment: discountAmount,
        code: storedCode,
      });
      setDiscountCode(storedCode);
    } else {
      // Clear stored values if they belong to a different session
      localStorage.removeItem(APPLIED_DISCOUNT_KEY);
      localStorage.removeItem(APPLIED_DISCOUNT_RESERVATION_KEY);
      localStorage.removeItem(APPLIED_DISCOUNT_AMOUNT);
      localStorage.removeItem(APPLIED_DISCOUNT_SESSION_ID);
    }
  }, [subtotal, checkoutSession.id]);

  const rtlClass = dir === "rtl" ? "rtl-header" : "";
  const labelAlignment = dir === "rtl" ? "right" : "left";
  const modality = checkoutSession?.items[0]?.offer?.modality;
  let triggerCheckoutEvent: any;

  if (applyBenefitToOfferLoading) {
    return <LoadingContent text={t("loadingPage_loadingText_loading")} />;
  }

  const handleApplyDiscount = () => {
    if (!discountCode) return;
    setIsApplyingDiscount(true);
    setStatusMessage(null);
    const appliedCode = discountCode; // Store the code before applying
    applyDiscount({
      discountCode,
      sessionId: checkoutSession.id,
    })
      .then((result) => {
        if (result.success && result.discountDetails) {
          setAppliedDiscount({
            reservationId: result.discountDetails.reservationId,
            amount:
              Number(subtotal) - Number(result.discountDetails.discountAmount),
            estimatedPayment: result.discountDetails.discountAmount,
            code: appliedCode, // Store the code with the discount details
          });
          localStorage.setItem(APPLIED_DISCOUNT_KEY, appliedCode); // Store the code
          setDiscountCode(appliedCode); // Keep the code visible in input
          setStatusMessage({
            text: t(
              "preCheckout_finalizeCheckout_discount_success",
              "Discount applied successfully"
            ),
            isError: false,
          });
        } else {
          setStatusMessage({
            text:
              result.message ||
              t(
                "preCheckout_finalizeCheckout_discount_error",
                "Failed to apply discount"
              ),
            isError: true,
          });
        }
      })
      .catch((error) => {
        setAppliedDiscount(null);
        setStatusMessage({
          text:
            error.message ||
            t(
              "preCheckout_finalizeCheckout_discount_error",
              "Failed to apply discount"
            ),
          isError: true,
        });
      })
      .finally(() => {
        setIsApplyingDiscount(false);
      });
  };

  const handleRemoveDiscount = () => {
    if (!appliedDiscount?.reservationId) return;
    setStatusMessage(null);
    cancelDiscountReservation(appliedDiscount.reservationId)
      .then((result) => {
        if (result.success) {
          setAppliedDiscount(null);
          localStorage.removeItem(APPLIED_DISCOUNT_KEY);
          setDiscountCode("");
          setStatusMessage({
            text: t(
              "preCheckout_finalizeCheckout_discount_removed",
              "Discount removed from your cart"
            ),
            isError: false,
          });
        } else {
          setStatusMessage({
            text:
              result.message ||
              t(
                "preCheckout_finalizeCheckout_discount_cancel_error",
                "Failed to cancel discount"
              ),
            isError: true,
          });
        }
      })
      .catch((error) => {
        setStatusMessage({
          text:
            error.message ||
            t(
              "preCheckout_finalizeCheckout_discount_cancel_error",
              "Failed to cancel discount"
            ),
          isError: true,
        });
      });
  };

  return (
    <SpaceBetween size="m">
      <Container data-testid="finalize_checkout_container">
        <SpaceBetween size="m">
          <Header className={rtlClass} variant="h2">
            {t("preCheckout_finalizeCheckout_summary")}
          </Header>

          {checkoutSession.type === CheckoutSessionType.NEW_ORDER ? (
            <>
              {modality === "AWS_JAM" && (
                <Grid
                  data-testid="subtotal_wrapper"
                  gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
                >
                  <Box
                    data-testid="preCheckout_price_per_participant_key"
                    textAlign="left"
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {t("postCheckout_orderSummary_pricePerParticipant")}
                  </Box>
                  <Box
                    data-testid="preCheckout_price_per_participant_value"
                    textAlign="right"
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {t("preCheckout_finalizeCheckout_subtotal_pricePerUnit", {
                      pricePerUnit,
                    })}
                    <Box
                      variant="span"
                      color="text-body-secondary"
                      fontSize="heading-s"
                    >
                      {t("preCheckout_finalizeCheckout_subtotal_quantity", {
                        quantity,
                      })}
                    </Box>
                  </Box>
                </Grid>
              )}

              {isFreeTrial && (
                <Grid
                  className={rtlClass}
                  data-testid="pre_checkout_free_trial_days_wrapper"
                  gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
                >
                  <Box
                    textAlign={labelAlignment}
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {t(
                      "preCheckout_finalizeCheckout_discount_free_trial_label"
                    )}
                  </Box>
                  <Box
                    textAlign="right"
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {t(
                      "preCheckout_finalizeCheckout_discount_freeTrial_duration",
                      {
                        freeTrialDuration: Number.isInteger(freeTrialDuration)
                          ? freeTrialDuration
                          : (freeTrialDuration as number).toFixed(2),
                      }
                    )}
                  </Box>
                </Grid>
              )}

              <Grid
                className={rtlClass}
                data-testid="subtotal_wrapper"
                gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
              >
                <Box
                  textAlign={labelAlignment}
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                >
                  {t("preCheckout_finalizeCheckout_subtotal_label")}
                </Box>
                <Box
                  textAlign="right"
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                >
                  {t("preCheckout_finalizeCheckout_subtotal_installmentPlan", {
                    subtotal,
                  })}
                </Box>
              </Grid>
              {checkoutSession.user?.id &&
                ENABLE_DISCOUNT_FEATURE[getNodeEnvironment()] && (
                  <Grid
                    className={rtlClass}
                    data-testid="discount_code_section"
                    gridDefinition={[{ colspan: 12 }]}
                  >
                    <Box>
                      <FormField
                        label={t(
                          "preCheckout_finalizeCheckout_discount_code_label",
                          "Discount code"
                        )}
                      >
                        <Grid gridDefinition={[{ colspan: 8 }, { colspan: 4 }]}>
                          <Input
                            data-testid="discount_code_input"
                            value={discountCode}
                            onChange={({ detail }) =>
                              setDiscountCode(detail.value)
                            }
                            placeholder={t(
                              "preCheckout_finalizeCheckout_discount_code_placeholder",
                              "Enter discount code"
                            )}
                            disabled={appliedDiscount !== null}
                          />
                          {appliedDiscount ? (
                            <Button
                              data-testid="remove_discount_button"
                              onClick={handleRemoveDiscount}
                              formAction="none"
                            >
                              {t(
                                "preCheckout_finalizeCheckout_remove_discount_button",
                                "Remove"
                              )}
                            </Button>
                          ) : (
                            <Button
                              data-testid="apply_discount_button"
                              onClick={handleApplyDiscount}
                              loading={isApplyingDiscount}
                              disabled={!discountCode || isApplyingDiscount}
                              formAction="none"
                            >
                              {t(
                                "preCheckout_finalizeCheckout_apply_discount_button",
                                "Apply"
                              )}
                            </Button>
                          )}
                        </Grid>
                      </FormField>
                      {statusMessage && (
                        <StatusMessage
                          isError={statusMessage.isError}
                          data-testid="status-message"
                        >
                          {statusMessage.text}
                        </StatusMessage>
                      )}
                    </Box>
                  </Grid>
                )}

              {(isPromotional || appliedDiscount) && (
                <Grid
                  className={rtlClass}
                  data-testid="pre_checkout_promotional_discount_wrapper"
                  gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
                >
                  <Box
                    textAlign={labelAlignment}
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {appliedDiscount
                      ? t(
                          "preCheckout_finalizeCheckout_discount_label",
                          "Discount Applied"
                        )
                      : t(
                          "preCheckout_finalizeCheckout_promotional_discount_label"
                        )}
                  </Box>
                  <Box
                    textAlign="right"
                    fontSize="heading-m"
                    padding={{ vertical: "xxs" }}
                  >
                    {appliedDiscount
                      ? `$${(
                          Number(subtotal) -
                          Number(appliedDiscount.estimatedPayment)
                        ).toFixed(2)}`
                      : t(
                          "preCheckout_finalizeCheckout_promotional_discount_value",
                          {
                            promotionalDiscount: promotionalDiscount,
                          }
                        )}
                  </Box>
                </Grid>
              )}

              <Grid
                className={rtlClass}
                data-testid="estimated_total_wrapper"
                gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
              >
                <Box
                  textAlign={labelAlignment}
                  data-testid="preCheckout_estimated_payments_key"
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                  fontWeight={modality === "DIGITAL" ? undefined : "bold"}
                >
                  {modality === "DIGITAL"
                    ? t(
                        "preCheckout_finalizeCheckout_estimatedTotal_digital_label"
                      )
                    : t("preCheckout_finalizeCheckout_estimatedTotal_label")}
                </Box>
                <Box
                  textAlign="right"
                  data-testid="preCheckout_estimated_payments_value"
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                  fontWeight={modality === "DIGITAL" ? undefined : "bold"}
                >
                  {modality === "DIGITAL"
                    ? t(
                        "preCheckout_finalizeCheckout_estimatedTotal_digital_installmentPlan",
                        {
                          periodAmount: appliedDiscount
                            ? appliedDiscount.estimatedPayment
                            : estimatedTotalPeriodAmount,
                        }
                      )
                    : t(
                        "preCheckout_finalizeCheckout_estimatedTotal_installmentPlan",
                        {
                          displayPeriod: t(displayPeriodIntlKey),
                          periodAmount: appliedDiscount
                            ? appliedDiscount.estimatedPayment
                            : estimatedTotalPeriodAmount,
                        }
                      )}
                  *
                </Box>
              </Grid>

              {isPromotional && !isFreeTrial && (
                <Alert
                  data-testid="pre_checkout_finalize_alert"
                  statusIconAriaLabel="Info"
                >
                  <Box
                    variant="span"
                    color="text-body-secondary"
                    data-testid="pre_checkout_finalize_alert_promotional"
                  >
                    <div dir={dir}>
                      {t(
                        "preCheckout_finalizeCheckout_nonfreeTrial_promotional_alert",
                        {
                          promotionalEndsDate: promotionalEndsDate,
                        }
                      )}
                    </div>
                  </Box>
                </Alert>
              )}

              {isFreeTrial && (
                <>
                  <Grid
                    className={rtlClass}
                    data-testid="pre_checkout_free_trial_estimated_total_wrapper"
                    gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
                  >
                    <Box
                      textAlign={labelAlignment}
                      data-testid="pre_checkout_free_trial_estimated_total_key"
                      fontSize="heading-m"
                      fontWeight="bold"
                      padding={{ vertical: "xxs" }}
                    >
                      {t(
                        "preCheckout_finalizeCheckout_freeTrial_estimatedTotal_label"
                      )}
                    </Box>
                    <Box
                      textAlign="right"
                      data-testid="pre_checkout_free_trial_estimated_total_value"
                      fontSize="heading-m"
                      fontWeight="bold"
                      padding={{ vertical: "xxs" }}
                    >
                      {t(
                        "preCheckout_finalizeCheckout_freeTrial_estimatedTotal_value"
                      )}
                    </Box>
                  </Grid>
                  <Alert
                    data-testid="pre_checkout_finalize_alert"
                    statusIconAriaLabel="Info"
                  >
                    <Box variant="span" color="text-body-secondary">
                      {isPromotional ? (
                        <div
                          data-testid="pre_checkout_finalize_alert_free_trial_and_promotional"
                          dir={dir}
                        >
                          {t(
                            "preCheckout_finalizeCheckout_freeTrial_promotional_alert",
                            {
                              freeTrialEndDate: freeTrialEndsDate,
                              promotionalEndsDate: promotionalEndsDate,
                            }
                          )}
                        </div>
                      ) : (
                        <div
                          data-testid="pre_checkout_finalize_alert_free_trial"
                          dir={dir}
                        >
                          {t("preCheckout_finalizeCheckout_freeTrial_endDate", {
                            freeTrialEndDate: freeTrialEndsDate,
                          })}
                        </div>
                      )}
                    </Box>
                  </Alert>
                </>
              )}
            </>
          ) : checkoutSession.type ===
            CheckoutSessionType.REPLACE_ORDER_ITEM ? (
            <>
              <Grid
                className={rtlClass}
                data-testid="previous_estimated_total_wrapper"
                gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
              >
                <Box
                  textAlign="left"
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                >
                  {t(
                    "preCheckout_finalizeCheckout_previousEstimatedTotal_label"
                  )}
                </Box>
                <Box
                  textAlign="right"
                  fontSize="heading-m"
                  padding={{ vertical: "xxs" }}
                >
                  {t(
                    "preCheckout_finalizeCheckout_estimatedTotal_installmentPlan",
                    {
                      displayPeriod: t(displayPeriodIntlKey),
                      periodAmount: previousEstimatedTotalPeriodAmount,
                    }
                  )}
                </Box>
              </Grid>
              <Grid
                className={rtlClass}
                data-testid="new_estimated_total_wrapper"
                gridDefinition={[{ colspan: 6 }, { colspan: 6 }]}
              >
                <Box
                  textAlign="left"
                  data-testid="preCheckout_new_estimated_total_key"
                  fontSize="heading-m"
                  fontWeight="bold"
                  padding={{ vertical: "xxs" }}
                >
                  {t("preCheckout_finalizeCheckout_newEstimatedTotal_label")}
                </Box>
                <Box
                  textAlign="right"
                  data-testid="preCheckout_new_estimated_total_value"
                  fontSize="heading-m"
                  fontWeight="bold"
                  padding={{ vertical: "xxs" }}
                >
                  {t(
                    "preCheckout_finalizeCheckout_estimatedTotal_installmentPlan",
                    {
                      displayPeriod: t(displayPeriodIntlKey),
                      periodAmount: estimatedTotalPeriodAmount,
                    }
                  )}
                  *
                </Box>
              </Grid>
            </>
          ) : (
            <Fragment />
          )}

          {shouldShowContinueToAws ? (
            <>
              <SubmitButton
                variant="primary"
                formAction="none"
                onClick={onClickContinueToAws}
                data-testid="finalize_checkout_continue_to_aws"
              >
                <div dir={dir}>
                  {t(
                    "preCheckout_finalizeCheckout_awsAccount_continue_button",
                    "Continue to AWS"
                  )}
                </div>
              </SubmitButton>
              <Box
                textAlign={labelAlignment}
                variant="span"
                color="text-body-secondary"
              >
                <div dir={dir}>
                  {t(
                    "preCheckout_finalizeCheckout_awsAccount_continue_description"
                  )}
                </div>
              </Box>
            </>
          ) : (
            <>
              {finalizeCheckoutSessionComponent.terms.map((term, idx) => (
                <Term key={`term-${idx}`} term={term} />
              ))}
              <Checkbox
                data-testid="terms_checkbox"
                onChange={({ detail }) => setIsAgreedTos(!isAgreedTos)}
                checked={isAgreedTos}
              >
                <div dir={dir}>{t("preCheckout_checkbox_text")}</div>
              </Checkbox>
              <SubmitButton
                data-testid="finalize_checkout_submit"
                variant="primary"
                formAction="none"
                disabled={
                  !isAgreedTos ||
                  (isFtv2SupportEnabled() && isFreeTrialV2Account)
                }
                disabledReason={
                  isFtv2SupportEnabled() && isFreeTrialV2Account
                    ? t("preCheckout_upgradeAwsAccount_error_box_text")
                    : ""
                }
                onClick={() => {
                  finalizeSession();
                  if (modality === "DIGITAL") {
                    const subscriptionPeriod = displayPeriodIntlKey.includes(
                      "month"
                    )
                      ? "Monthly"
                      : "Yearly";
                    triggerCheckoutEvent = createCheckoutEventTrigger(
                      checkoutSession,
                      "toolComplete",
                      "Purchase",
                      "B2I",
                      subscriptionPeriod
                    );
                  }
                  if (modality === "AWS_JAM") {
                    triggerCheckoutEvent = createCheckoutEventTrigger(
                      checkoutSession,
                      "toolComplete",
                      "Confirm_purchase",
                      "Event_checkout"
                    );
                  }
                  if (typeof triggerCheckoutEvent === "function") {
                    triggerCheckoutEvent();
                  }
                }}
              >
                <div dir={dir}>
                  {finalizeCheckoutSessionComponent.submitText}
                </div>
              </SubmitButton>
            </>
          )}
        </SpaceBetween>
      </Container>
      <Box
        textAlign={labelAlignment}
        variant="span"
        color="text-body-secondary"
      >
        <div dir={dir}>{t("preCheckout_finalizeCheckout_finalTaxes_note")}</div>
      </Box>
    </SpaceBetween>
  );
};

export default FinalizeCheckoutSession;
